import { useEffect, useRef, useState } from 'react';
import { Toolbar } from '../components/Toolbar';
import { TabBar } from '../components/TabBar';
import { FormatBar } from '../components/FormatBar';
import { StatusBar } from '../components/StatusBar';
import { SplitView } from '../components/SplitView';
import { Sidebar } from '../components/Sidebar';
import { CommandPalette } from '../components/CommandPalette';
import { ShareDialog } from '../components/ShareDialog';
import { ShareManagerDialog } from '../components/ShareManagerDialog';
import { ShareOptionsDialog } from '../components/ShareOptionsDialog';
import { OutlinePanel } from '../components/OutlinePanel';
import { VaultPanel } from '../components/VaultPanel';
import { SettingsDialog } from '../components/SettingsDialog';
import { AuthDialog } from '../components/AuthDialog';
import { AccountDialog } from '../components/AccountDialog';
import { HistoryDialog } from '../components/HistoryDialog';
import { TrashDialog } from '../components/TrashDialog';
import { TemplateDialog } from '../components/TemplateDialog';
import { MoveToFolderDialog } from '../components/MoveToFolderDialog';
import { PublishSiteDialog } from '../components/PublishSiteDialog';
import { ImportDialog } from '../components/ImportDialog';
import { ImportUrlDialog } from '../components/ImportUrlDialog';
import { TagsEditorDialog } from '../components/TagsEditorDialog';
import { ShortcutsDialog } from '../components/ShortcutsDialog';
import { UndoToast } from '../components/UndoToast';
import { PromptDialog, ConfirmDialog } from '../components/PromptDialog';
import { SyncToast } from '../components/SyncToast';
import { PwaPrompt } from '../components/PwaPrompt';
import { useAuthSync } from '../hooks/useAuthSync';
import { useBackupScheduler } from '../hooks/useBackupScheduler';
import { useFolderSync } from '../hooks/useFolderSync';
import type { EditorHandle } from '../components/Editor';
import { useStore } from '../store/useStore';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useDocController } from '../hooks/useDocController';
import { DocControllerProvider } from '../hooks/DocControllerContext';
import { prepareImport } from '../lib/import';
import { useT } from '../i18n/useT';
import { askNotice } from '../lib/dialogs';

export function EditorPage() {
  const t = useT();
  const editorRef = useRef<EditorHandle>(null);
  const sidebarOpen = useStore((s) => s.sidebarOpen);
  const isNarrow = useStore((s) => s.isNarrow);
  const focusMode = useStore((s) => s.focusMode);
  const setOpenSearch = useStore((s) => s.setOpenSearch);
  const setGoToLine = useStore((s) => s.setGoToLine);
  const setRunTable = useStore((s) => s.setRunTable);
  const controller = useDocController(editorRef);
  useKeyboardShortcuts(editorRef, controller);
  useAuthSync();
  useBackupScheduler();
  useFolderSync();
  const [dragging, setDragging] = useState(false);

  // Register actions so the sidebar + palette can trigger editor-owned UI.
  useEffect(() => {
    setOpenSearch(() => editorRef.current?.openSearch());
    setRunTable((command) => editorRef.current?.table(command) ?? false);
    setGoToLine((line: number) => {
      editorRef.current?.scrollToLine(line);
      editorRef.current?.focus();
    });
    return () => {
      setOpenSearch(null);
      setRunTable(null);
      setGoToLine(null);
    };
  }, [setOpenSearch, setGoToLine, setRunTable]);

  // PWA file handler: when the OS opens .md files "with markbird", import them.
  useEffect(() => {
    interface LaunchParams {
      files?: FileSystemFileHandle[];
    }
    interface LaunchQueue {
      setConsumer: (cb: (params: LaunchParams) => void) => void;
    }
    const lq = (window as unknown as { launchQueue?: LaunchQueue }).launchQueue;
    if (!lq) return;
    lq.setConsumer((params) => {
      void (async () => {
        const handles = params.files ?? [];
        const files: File[] = [];
        for (const h of handles) files.push(await h.getFile());
        if (files.length) void controller.importFiles(files);
      })();
    });
  }, [controller]);

  // Responsive: on narrow viewports collapse the sidebar first (once) and force
  // a single pane (SplitView reads `isNarrow`).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)');
    const apply = (narrow: boolean) => {
      const s = useStore.getState();
      s.setNarrow(narrow);
      if (narrow && s.sidebarOpen) s.setSidebar(false);
    };
    apply(mq.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Esc leaves focus (zen) mode.
  useEffect(() => {
    if (!focusMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') useStore.getState().setFocusMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusMode]);

  return (
    <DocControllerProvider value={controller}>
      <div
        className={`mb-app ${focusMode ? 'mb-app--focus' : ''}`}
        onDragOver={(e) => {
          if (Array.from(e.dataTransfer.items).some((i) => i.kind === 'file')) {
            e.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          // Snapshot the list: the DataTransfer is emptied once this handler returns.
          const dropped = Array.from(e.dataTransfer.files);
          void prepareImport(dropped).then(({ accepted, rejected, unreadable }) => {
            if (accepted.length) {
              void controller.importFiles(accepted).then((failed) => {
                if (failed.length === 0) return;
                void askNotice({
                  title: t('import.failedTitle'),
                  body: t('import.failed', { files: failed.join(', ') }),
                });
              });
            }
            // Dropping a PDF on the window used to do nothing whatsoever. Opening
            // the dialog gives that silence somewhere to become a sentence.
            if (rejected.length || unreadable.length) useStore.getState().setImportOpen(true);
          });
        }}
      >
        <Toolbar />
        <div
          className="mb-body"
          style={{ gridTemplateColumns: sidebarOpen && !isNarrow ? 'auto 1fr' : '1fr' }}
        >
          {sidebarOpen && !isNarrow && <Sidebar />}
          <main className="mb-main">
            <TabBar />
            <FormatBar editorRef={editorRef} />
            <div className="mb-main-inner">
              <SplitView editorRef={editorRef} onActivity={controller.persistSession} />
              <OutlinePanel />
              {/* Renders nothing unless vault mode has a panel open. */}
              <VaultPanel />
            </div>
          </main>
        </div>
        {/* On narrow screens the library is an overlay drawer, not a grid column. */}
        {sidebarOpen && isNarrow && (
          <>
            <div
              className="mb-drawer-backdrop"
              onClick={() => useStore.getState().setSidebar(false)}
            />
            <Sidebar drawer onClose={() => useStore.getState().setSidebar(false)} />
          </>
        )}
        <StatusBar />
        <CommandPalette />
        <ShareDialog />
        <ShareManagerDialog />
        <ShareOptionsDialog />
        <SettingsDialog />
        <AuthDialog />
        <AccountDialog />
        <HistoryDialog />
        <TrashDialog />
        <TemplateDialog />
        <MoveToFolderDialog />
        <PublishSiteDialog />
        <ImportDialog />
        <ImportUrlDialog />
        <TagsEditorDialog />
        <ShortcutsDialog />
        <PromptDialog />
        <ConfirmDialog />
        <UndoToast />
        <SyncToast />
        <PwaPrompt />

        {focusMode && (
          <button
            className="mb-focus-exit"
            onClick={() => useStore.getState().setFocusMode(false)}
            title={t('focus.exitTitle')}
          >
            {t('focus.exit')}
          </button>
        )}

        {dragging && (
          <div className="mb-dropzone" aria-hidden="true">
            <div className="mb-dropzone__inner">{t('import.overlay')}</div>
          </div>
        )}
      </div>
    </DocControllerProvider>
  );
}
